import router from "@/__main__/router.mjs";
import { APP_SCROLLER } from "@/app/constants.mjs";
import ANALYTIC_EVENTS from "@/feature-analytics/analytic-events.mjs";
import { refs__PRIVATE as refs } from "@/feature-analytics/tracker.mjs";
import { devDebug } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";

const SCROLL_WINDOW = 10 * 1000; // 10 seconds

let init: boolean;

let isScrolling: boolean;
let scrollTimeout: Timeout;
let scrollStartTime: number;

let totalTimeScrolledInWindow: number = 0;
let routeStart: string;
let routeParamsStart: string[];
let urlStart: string;

export async function trackScrolling() {
  // EVENT_APP_RENDER is fired twice in dev, we only want to set this up once
  if (init) return;
  init = true;

  let scroller = globals.document.querySelector(`.${APP_SCROLLER}`);
  if (!scroller) {
    scroller = await new Promise((resolve) => {
      const obs = new MutationObserver((mutations) => {
        for (const { addedNodes } of mutations) {
          for (const node of addedNodes) {
            if (!(node instanceof HTMLElement)) continue;
            if (node.classList.contains(APP_SCROLLER)) {
              resolve(node);
            }
            const descendant = node.querySelector(`.${APP_SCROLLER}`);
            if (descendant) {
              resolve(descendant);
            }
          }
        }
      });
      obs.observe(globals.document.body, {
        childList: true,
        subtree: true,
      });
    });
  }
  scroller.addEventListener("scroll", handleScroll);
}

function handleScroll() {
  if (!isScrolling) {
    devDebug("Scrolling: started");
    isScrolling = true;
    scrollStartTime = Date.now();

    if (totalTimeScrolledInWindow === 0) {
      startScrollWindow();
    }
  }

  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    isScrolling = false;
    const thisScrollTime = Date.now() - scrollStartTime;
    totalTimeScrolledInWindow += thisScrollTime;
    devDebug(`Scrolling: stopped, scrolled for: ${thisScrollTime}ms`);
  }, 50);
}

function startScrollWindow() {
  devDebug(`Scrolling: new scroll window for ${SCROLL_WINDOW}ms`);
  routeStart = stringifyPath(router.route.path);
  routeParamsStart = router.route.parameters;
  urlStart = globals.location.href;
  setTimeout(handleScrollWindowEnding, SCROLL_WINDOW);
}

async function handleScrollWindowEnding() {
  if (isScrolling) {
    const thisScrollTime = Date.now() - scrollStartTime;
    totalTimeScrolledInWindow += thisScrollTime;
    devDebug(
      `Scrolling: still scrolling while window ended, adding: ${thisScrollTime}ms for this window`,
    );
    scrollStartTime = Date.now();
    startScrollWindow();
  }

  devDebug(
    `Scrolling: window ended, scrolled for: ${totalTimeScrolledInWindow}ms`,
  );
  const timeScrolled = totalTimeScrolledInWindow;
  totalTimeScrolledInWindow = 0;

  await refs.trackEvent(ANALYTIC_EVENTS.SCROLLING, {
    timeScrolled,
    scrollWindow: SCROLL_WINDOW,
    routeStart,
    routeParamsStart,
    urlStart,
    routeEnd: stringifyPath(router.route.path),
    routeParamsEnd: router.route.parameters,
    urlEnd: globals.location.href,
  });
}

// Setup actually happens through tracker.mjs and calling trackScrolling()
export function setup() {}

export function teardown() {
  if (!init) return;

  const [scroller] = globals.document.getElementsByClassName(APP_SCROLLER);
  scroller.removeEventListener("scroll", handleScroll);
}
